// src/pages/Home.js - Institutional Parent Company Landing Page
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, ArrowRight } from 'lucide-react';
import BlackoutGlobe from '../components/BlackoutGlobe';

const Home = ({ darkMode }) => {
  const navigate = useNavigate();

  return (
    <div className={darkMode ? 'bg-black' : 'bg-white'}>
      {/* Hero Section - Understated, Mission-focused */}
      <section className="relative py-24 md:py-32 lg:py-40">
        <div className="max-w-wider mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className={`text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6 tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                The future is what we build
              </h1>
              <p className={`text-lg mb-10 leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                The technology that will run tomorrow's world is being built today, by our researchers and engineers, for industries and for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/about')}
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 font-medium transition-colors ${
                    darkMode
                      ? 'bg-white text-black hover:bg-gray-200'
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  About Us
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
            <div className="hidden lg:block relative w-full h-[480px]">
              <BlackoutGlobe darkMode={darkMode} />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className={`py-24 border-t ${darkMode ? 'border-gray-900 bg-black' : 'border-gray-100 bg-white'}`}>
        <div className="max-w-wider mx-auto px-6">
          <div className="max-w-3xl">
            <p className={`text-sm font-medium tracking-wider uppercase mb-6 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Our Focus
            </p>
            <p className={`text-2xl md:text-3xl font-light leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Problems worth solving rarely have quick answers. We take the time
              to understand them properly.
            </p>
          </div>
        </div>
      </section>

      {/* Research Areas - Simplified, non-salesy */}
      <section className={`py-24 ${darkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-wider mx-auto px-6">
          <div className="mb-16">
            <p className={`text-sm font-medium tracking-wider uppercase mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Research Areas
            </p>
            <h2 className={`text-3xl md:text-4xl font-light ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              What We Work On
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className={`text-xl font-light mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Distributed Intelligence
              </h3>
              <p className={`text-base leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Architectures for computation that spans data centers, edge nodes, and
                embedded devices. Systems that think and act across distributed environments.
              </p>
            </div>
            <div>
              <h3 className={`text-xl font-light mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Cyber-Physical Systems
              </h3>
              <p className={`text-base leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Integration of computational intelligence with physical infrastructure: sensors,
                actuators, and control systems operating autonomously in the real world.
              </p>
            </div>
            <div>
              <h3 className={`text-xl font-light mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Applied AI Research
              </h3>
              <p className={`text-base leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Machine learning systems designed for constrained environments: efficient inference,
                on-device learning, and models that work without cloud connectivity.
              </p>
            </div>
            <div>
              <h3 className={`text-xl font-light mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Resilient Computing
              </h3>
              <p className={`text-base leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Systems engineered for reliability and security in critical applications: fault
                tolerance, secure architectures, and deterministic performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Arm - Extheon */}
      <section id="portfolio" className={`py-24 border-t ${darkMode ? 'border-gray-900 bg-black' : 'border-gray-100 bg-white'}`}>
        <div className="max-w-wider mx-auto px-6">
          <div className="mb-16">
            <p className={`text-sm font-medium tracking-wider uppercase mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Commercial Arm
            </p>
            <h2 className={`text-3xl md:text-4xl font-light mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Extheon
            </h2>
            <p className={`text-lg max-w-2xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Our technology reaches the market through Extheon.
            </p>
          </div>

          <div className={`border ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="grid lg:grid-cols-2">
              <div className="p-8 md:p-12">
                <p className={`text-base leading-relaxed mb-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Where our research becomes product.
                </p>
                <a
                  href="https://extheon.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                    darkMode
                      ? 'bg-white text-black hover:bg-gray-200'
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  Visit extheon.com
                  <ExternalLink size={16} />
                </a>
              </div>
              <div className={`flex items-center justify-center p-12 ${darkMode ? 'bg-gray-950 border-l border-gray-800' : 'bg-gray-50 border-l border-gray-200'}`}>
                <div className="text-center">
                  <div className={`text-3xl md:text-4xl font-light tracking-wider mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>EXTHEON</div>
                  <div className={`text-xs tracking-widest ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>PRODUCTS & SERVICES</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className={`py-32 border-t ${darkMode ? 'border-gray-900 bg-black' : 'border-gray-100 bg-white'}`}>
        <div className="max-w-wider mx-auto px-6">
          <div className="max-w-3xl">
            <h2 className={`text-5xl font-light tracking-tight mb-8 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Get in Touch
            </h2>
            <p className={`text-lg font-light mb-12 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Research collaborations, partnerships, or careers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/contact')}
                className={`px-10 py-4 font-light transition-colors text-center ${
                  darkMode
                    ? 'bg-white text-black hover:bg-gray-100'
                    : 'bg-black text-white hover:bg-gray-900'
                }`}
              >
                Contact Us
              </button>
              <button
                onClick={() => navigate('/careers')}
                className={`px-10 py-4 font-light border transition-colors text-center ${
                  darkMode
                    ? 'border-white text-white hover:bg-white hover:text-black'
                    : 'border-black text-black hover:bg-black hover:text-white'
                }`}
              >
                Careers
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
